/* Pawling geography: listing locations, never a legal eligibility decision. */
(function(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.PawlingGeography = api;
})(typeof window !== 'undefined' ? window : this, function() {
  'use strict';
  const states = [["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"], ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"], ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"], ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"]].map(([code, name]) => ({code, name}));
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const canonical = value => {
    const key = normalize(value);
    if (['washington dc', 'washington d c', 'd c', 'district of columbia'].includes(key)) return 'DC';
    return states.find(s => key === s.code.toLowerCase() || key === normalize(s.name))?.code || '';
  };
  const stateName = value => states.find(s => s.code === canonical(value))?.name || '';
  function parseRegion(value) {
    const input = String(value || '').trim();
    const result = (state, label) => ({state, city:normalize(label), label:label.replace(/[,\s]+$/g, '')});
    const tail = input.match(/(?:^|[,\s])(D\.?\s*C\.?|[A-Za-z]{2})(?:\s+\d{5}(?:-\d{4})?)?\s*$/i);
    if (tail && canonical(tail[1])) return result(canonical(tail[1]), input.slice(0, tail.index));
    for (const s of [...states].sort((a,b) => b.name.length-a.name.length)) {
      const suffix = new RegExp('(?:^|[,\\s])' + s.name + '(?:\\s+\\d{5}(?:-\\d{4})?)?\\s*$', 'i');
      const match = input.match(suffix);
      if (match) return result(s.code, input.slice(0, match.index));
    }
    if (canonical(input)) return result(canonical(input), '');
    return result('', input);
  }
  // Backfills apply only to these checked listing IDs AND unchanged locations.
  const legacy = {
    'live-mini-toy-schnauzer': {location:'scottsdale', state:'AZ'},
    'live-puppy-for-sale': {location:'phoenix chandler', state:'AZ', cities:['Phoenix','Chandler']},
    'live-dog': {location:'sarasota', state:'FL'}
  };
  function resolve(location, id, explicitState) {
    if (explicitState) return canonical(explicitState);
    const parsed = parseRegion(location).state;
    if (parsed) return parsed;
    const prior = legacy[id];
    return prior && normalize(location) === prior.location ? prior.state : '';
  }
  function citiesFor(location, id, explicitCity, explicitState) {
    if (String(explicitCity || '').trim()) return [String(explicitCity).trim()];
    const prior = legacy[id];
    if (prior?.cities && normalize(location) === prior.location && (!explicitState || canonical(explicitState) === prior.state)) return [...prior.cities];
    const label = parseRegion(location).label;
    return label ? [label] : [];
  }
  function matches(location, query, selectedState, id, explicitState, explicitCity, exactCity=false) {
    const wanted = canonical(selectedState);
    if (selectedState && !wanted) return false;
    const actual = resolve(location, id, explicitState);
    // City names such as Washington or Indiana must not be interpreted as states here.
    if (exactCity && query) return !!wanted && actual === wanted && citiesFor(location,id,explicitCity,explicitState).some(city => normalize(city) === normalize(query));
    const parsed = parseRegion(query);
    if (wanted && parsed.state && wanted !== parsed.state) return false;
    const target = wanted || parsed.state;
    if (target && actual !== target) return false;
    // Typed area searches remain flexible.
    const haystack = normalize([location,explicitCity].filter(Boolean).join(' '));
    return !parsed.city || parsed.city.split(/\s+/).every(token => haystack.includes(token));
  }
  function buildIndex(rows) {
    const closed = new Set(['placed','sold','adopted','closed','inactive','expired']);
    const counts = {}, cities = new Map(), seen = new Set();
    let total = 0, unknown = 0;
    rows.forEach(row => {
      if (row.active === false || row.active === 'false' || closed.has(normalize(row.status))) return;
      if (row.id && seen.has(row.id)) return;
      if (row.id) seen.add(row.id);
      total++;
      const state = resolve(row.location,row.id,row.state);
      if (!state) { unknown++; return; }
      counts[state] = (counts[state] || 0) + 1;
      const citySeen = new Set();
      citiesFor(row.location,row.id,row.city,row.state).forEach(label => {
        const key = normalize(label);
        if (!key || citySeen.has(key)) return;
        citySeen.add(key);
        const indexKey = state + ':' + key;
        const city = cities.get(indexKey) || {state,key,label,count:0};
        city.count++;
        cities.set(indexKey, city);
      });
    });
    return {total,unknown,counts,cities:[...cities.values()].sort((a,b) => a.label.localeCompare(b.label) || a.state.localeCompare(b.state))};
  }
  const searchForm = () => document.getElementById('PawlingHeroSearch');
  const formState = () => searchForm()?.elements.pawling_state.value || '';
  const formCity = () => searchForm()?.elements.pawling_location.value || '';
  const formExactCity = () => searchForm()?.dataset.pawlingCityExact === 'true';
  const formKeyword = () => searchForm()?.elements.pawling_keyword.value || '';
  function searchUrl(base, keyword, city, state, exactCity=false) {
    const url = new URL(base);
    for (const [key,value] of Object.entries({q:keyword,city,state,city_mode:exactCity && city ? 'exact' : ''})) {
      if (value) url.searchParams.set(key,value); else url.searchParams.delete(key);
    }
    url.searchParams.delete('saved');
    url.hash = keyword || city || state ? 'pet-search-results' : 'current-listings';
    return url;
  }
  function syncUrl(keyword, city, state) {
    try { window.history.replaceState(null,'',searchUrl(window.location.href,keyword,city,state,formExactCity())); }
    catch (_) { /* Offline previews may restrict history. */ }
  }
  function locationLink(state, city='') {
    return searchUrl(window.location.href,formKeyword(),city,state,!!city).href;
  }
  function cardLink(title, detail, state, city) {
    const link = document.createElement('a');
    link.href = locationLink(state,city || '');
    if (city === undefined) link.dataset.pawlingStateLink = state;
    else { link.dataset.pawlingCityLink = city; link.dataset.pawlingCityState = state; }
    const strong = document.createElement('strong'), small = document.createElement('span');
    strong.textContent = title; small.textContent = detail;
    link.append(strong,small);
    return link;
  }
  function activateTab(name, focus=false) {
    const directory = document.querySelector('[data-pawling-state-directory]');
    if (!directory) return;
    directory.querySelectorAll('[data-pawling-location-tab]').forEach(tab => {
      const selected = tab.dataset.pawlingLocationTab === name;
      tab.setAttribute('aria-selected',String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
      if (selected && focus) tab.focus({preventScroll:true});
    });
  }
  function renderCities(index) {
    const selected = formState(), valid = canonical(selected), filter = document.querySelector('[data-pawling-city-filter]');
    const query = normalize(filter?.value);
    const allCities = index.cities.filter(city => !selected || city.state === valid);
    const cities = allCities.filter(city => normalize(city.label).includes(query));
    const grid = document.querySelector('[data-pawling-city-links]');
    if (grid) {
      const links = cities.map(city => cardLink(selected ? city.label : `${city.label}, ${city.state}`,`${city.count} ${city.count === 1 ? 'listing' : 'listings'}`,city.state,city.label));
      grid.replaceChildren(...links);
      grid.querySelectorAll('[data-pawling-city-link]').forEach(link => {
        if (link.dataset.pawlingCityState === valid && normalize(link.dataset.pawlingCityLink) === normalize(formCity()) && (!formCity() || formExactCity())) link.setAttribute('aria-current','true');
      });
    }
    const title = document.querySelector('[data-pawling-city-title]');
    if (title) title.textContent = valid ? `Cities in ${stateName(valid)}` : 'Cities with current ads';
    const status = document.querySelector('[data-pawling-city-summary]');
    if (status) status.textContent = `${cities.length} ${cities.length === 1 ? 'city or area' : 'cities and areas'}${valid ? ' in ' + stateName(valid) : ' across states'}${query ? ' match your city search' : ' with current ads'}.`;
    const empty = document.querySelector('[data-pawling-city-empty]');
    if (empty) {
      empty.hidden = cities.length > 0;
      empty.textContent = selected && !valid ? 'Choose a valid state to browse cities.' : query && index.total > 0 ? 'No city names match. Try another name or clear the city search.' : 'No listings yet—be the first to post.';
    }
    const change = document.querySelector('[data-pawling-choose-state]');
    if (change) change.textContent = selected ? 'Change state' : 'Choose a state';
  }
  function decorate() {
    const cards = [...document.querySelectorAll('#current-listings [data-pawling-live-listing]')];
    const index = buildIndex(cards.map(card => ({id:card.dataset.id,location:card.dataset.searchLocation,state:card.dataset.searchState,city:card.dataset.searchCity,status:card.dataset.searchStatus,active:card.dataset.pawlingActive})));
    const selected = formState();
    document.querySelectorAll('[data-pawling-state-count]').forEach(el => {
      const count = index.counts[el.dataset.pawlingStateCount] || 0;
      el.textContent = count ? `${count} ${count === 1 ? 'listing' : 'listings'}` : 'No listings yet—be the first to post.';
    });
    const active = document.querySelector('[data-pawling-active-state-links]');
    const activeStates = states.filter(s => index.counts[s.code]);
    if (active) active.replaceChildren(...activeStates.map(s => cardLink(s.name,`${index.counts[s.code]} current ${index.counts[s.code] === 1 ? 'listing' : 'listings'}`,s.code)));
    const activeEmpty = document.querySelector('[data-pawling-active-state-empty]');
    if (activeEmpty) activeEmpty.hidden = activeStates.length > 0;
    const breadcrumb = document.querySelector('[data-pawling-location-breadcrumb]');
    if (breadcrumb) {
      const all = document.createElement('a'); all.href = locationLink(''); all.dataset.pawlingStateLink = ''; all.textContent = 'All states';
      const parts = [all];
      if (selected) {
        const state = document.createElement('a'); state.href = locationLink(selected); state.dataset.pawlingStateLink = selected; state.textContent = stateName(selected) || 'Choose a valid state';
        parts.push(document.createTextNode(' / '),state);
      }
      if (formCity()) { const city = document.createElement('span'); city.textContent = formCity(); city.setAttribute('aria-current','location'); parts.push(document.createTextNode(' / '),city); }
      breadcrumb.replaceChildren(...parts);
    }
    document.querySelectorAll('[data-pawling-state-link]').forEach(link => {
      link.href = locationLink(link.dataset.pawlingStateLink);
      if (selected === link.dataset.pawlingStateLink && !formCity()) link.setAttribute('aria-current','true'); else link.removeAttribute('aria-current');
    });
    const summary = document.querySelector('[data-pawling-state-summary]');
    if (summary) summary.textContent = `${index.total} current ${index.total === 1 ? 'listing' : 'listings'} across ${Object.keys(index.counts).length} ${Object.keys(index.counts).length === 1 ? 'state' : 'states'}.` + (index.unknown ? ` ${index.unknown} awaiting a state remain in All states.` : '');
    const choices = document.getElementById('PawlingCityOptions');
    if (choices) {
      const labels = [...new Set(index.cities.filter(city => !selected || city.state === canonical(selected)).map(city => city.label))];
      choices.replaceChildren(...labels.map(label => { const option = document.createElement('option'); option.value = label; return option; }));
    }
    renderCities(index);
  }
  function bind() {
    const directory = document.querySelector('[data-pawling-state-directory]');
    const form = searchForm();
    if (directory && form && !directory.dataset.bound) {
      directory.addEventListener('click',event => {
        if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const tab = event.target.closest('[data-pawling-location-tab]');
        if (tab) { activateTab(tab.dataset.pawlingLocationTab,true); return; }
        if (event.target.closest('[data-pawling-choose-state]')) { activateTab('states',true); return; }
        const cityLink = event.target.closest('[data-pawling-city-link]');
        const stateLink = event.target.closest('[data-pawling-state-link]');
        if (!cityLink && !stateLink) return;
        event.preventDefault();
        form.elements.pawling_state.value = cityLink ? cityLink.dataset.pawlingCityState : stateLink.dataset.pawlingStateLink;
        form.elements.pawling_location.value = cityLink ? cityLink.dataset.pawlingCityLink : '';
        form.dataset.pawlingCityExact = cityLink && cityLink.dataset.pawlingCityLink ? 'true' : 'false';
        const filter = directory.querySelector('[data-pawling-city-filter]');
        if (filter) filter.value = '';
        // Keep the visitor in the directory after choosing a state so they can choose a city next.
        form.dataset.pawlingKeepBrowse = cityLink ? 'false' : 'true';
        try { form.requestSubmit(); } finally { delete form.dataset.pawlingKeepBrowse; }
        decorate();
        if (stateLink) activateTab('cities',true);
        else document.getElementById('PawlingUnifiedResultsHeading')?.focus({preventScroll:true});
      });
      directory.addEventListener('keydown',event => {
        const tab = event.target.closest('[data-pawling-location-tab]');
        if (!tab || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault();
        const name = event.key === 'Home' ? 'states' : event.key === 'End' ? 'cities' : tab.dataset.pawlingLocationTab === 'states' ? 'cities' : 'states';
        activateTab(name,true);
      });
      directory.querySelector('[data-pawling-city-filter]')?.addEventListener('input',decorate);
      directory.dataset.bound = 'true';
      activateTab(formState() || formCity() ? 'cities' : 'states');
    }
    if (form && !form.dataset.geoBound) {
      form.elements.pawling_state.addEventListener('change',() => {
        form.elements.pawling_location.value = '';
        delete form.dataset.pawlingCityExact;
        const filter = document.querySelector('[data-pawling-city-filter]');
        if (filter) filter.value = '';
        decorate(); activateTab('cities');
      });
      form.elements.pawling_location.addEventListener('input',() => { delete form.dataset.pawlingCityExact; });
      form.dataset.geoBound = 'true';
    }
    decorate();
  }
  return {states,normalize,canonical,stateName,parseRegion,resolve,citiesFor,matches,buildIndex,formState,formCity,formExactCity,searchUrl,syncUrl,activateTab,decorate,bind};
});
